import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from 'src/app/api/auth.service';
import { LangService } from 'src/app/core/lang.service';
import { resolveExportRows } from 'src/app/scoring-leader-utils/sanitizeTableRows';
import { buildExportInfo, describeExportScope, readSnapshotContext } from 'src/app/scoring-leader-utils/exportInfo';

@Component({
  selector: 'export-table-contents',
  templateUrl: './export-table-contents.component.html',
  styleUrls: ['./export-table-contents.component.scss']
})
export class ExportTableContentsComponent implements OnInit, OnDestroy {

  constructor(
    private auth: AuthService,
    public lang: LangService,
    private host: ElementRef<HTMLElement>,
  ) { }

  isDownloadingData:boolean = false;
  /** Rows to export, ready-made by the panel (legacy). Ignored when `columnDefs` is bound. */
  @Input() tableContents = [];
  @Input() filename = '';
  @Input() toGenerateData = false;
  /** SEL-017 — names the report so several Export buttons on one page are told apart by name */
  @Input() reportName = '';
  /**
   * CTD FU-03 — grid-backed export. Bind `gridApi`, `columnDefs` and `records` and the button decides
   * the rows when it is pressed: the selected rows on screen, else the filtered and sorted rows.
   * A filter that hides every row exports nothing.
   */
  @Input() gridApi: any = null;
  @Input() columnDefs: any[] | null = null;
  @Input() records: any[] | null = null;
  /** Set false on a grid where selecting a row opens a detail panel or an editor, so it does not narrow the export. */
  @Input() useSelection = true;

  /** True for a few seconds after a press that found no rows to export. */
  hasNothingToExport = false;
  private nothingTimer: any = null;

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.clearNothingTimer();
  }

  private clearNothingTimer() {
    if (this.nothingTimer) clearTimeout(this.nothingTimer);
    this.nothingTimer = null;
  }

  /** The rows this button would download right now. */
  getRowsToExport(): any[] {
    return resolveExportRows({
      gridApi: this.gridApi,
      columnDefs: this.columnDefs,
      records: this.records,
      tableContents: this.tableContents,
      useSelection: this.useSelection,
    });
  }

  /**
   * CTD CONF-013 — what the workbook says about itself: the report, how its rows were chosen and the
   * snapshot of the statistics they came from (read from the report region this button sits in).
   */
  exportInfoFor(rows: any[]) {
    const source = {
      gridApi: this.gridApi,
      columnDefs: this.columnDefs,
      records: this.records,
      useSelection: this.useSelection,
    };
    return buildExportInfo({
      reportName: this.reportName,
      fileName: this.filename,
      scope: describeExportScope(source, rows.length),
      rowsAvailable: Array.isArray(this.columnDefs) && Array.isArray(this.records) ? this.records.length : null,
      context: readSnapshotContext(this.host?.nativeElement),
    });
  }

  async downloadData(){
    if (this.isDownloadingData) return;
    this.clearNothingTimer();
    this.hasNothingToExport = false;
    const rows = this.getRowsToExport();
    if (rows.length === 0) {
      // Nothing is sent: an empty workbook is not an export.
      this.hasNothingToExport = true;
      this.nothingTimer = setTimeout(() => { this.hasNothingToExport = false; this.nothingTimer = null; }, 5000);
      return;
    }
    this.isDownloadingData = true;
    try {
      const {url} = <any> await this.auth.jsonToExcel(rows, this.filename, this.exportInfoFor(rows));
      // downloadFile(url) // cant import?
      const a = document.createElement("a");
    a.setAttribute('data-testid', "export-table-contents-download-data-a-download");
    a.setAttribute('data-ui-name', "Download — download Data"); a.href = url; a.click(); // see above
    }
    finally {
      // A failed download must not leave the button disabled for good.
      this.isDownloadingData = false;
    }
  }
}
